import { gettext } from '@eventbrite/i18n';
import React, { ReactNode, useRef } from 'react';
import { Highlights, HighlightsProps } from '../../Highlights';
import { useIntersecting } from '../../hooks/useIntersecting';

import { EventDetailsSectionTitle } from '../EventDetails';
import { Agenda, AgendaWidget } from '../Widgets/Agenda';
import { Faq, FaqWidget } from '../Widgets/Faq';
import { Widget } from '../Widgets/types';
import { parseWidgets } from '../Widgets/utils';

export interface EventDescriptionProps extends Summary, LegacyDescription {
    shouldShowSalesEndedMessage: boolean | null;
    organizerSalesEndedMessage: string | null;
    salesEndedMessage: string | null;
    hasStructuredContent: boolean;
    structuredContent: StructuredContent;
}

type StructuredContent = {
    admat: Admat | null;
    hasAutogeneratedContent: boolean | null;
    modules: StructuredContentModule[];
    widgets: Widget[];
};

type Summary = {
    summary: string;
};

type LegacyDescription = {
    legacyDescription: string;
    highlights?: HighlightsProps;
};

interface AboutThisEventProps extends StructuredContent {
    hasStructuredContent: boolean;
    highlights?: HighlightsProps;
}

export enum StructuredContentModuleType {
    TEXT = 'text',
    IMAGE = 'image',
    VIDEO = 'video',
}

type StructuredContentModule = {
    type: StructuredContentModuleType;
} & (TextContentProps | ImageContentProps | VideoContentProps);

export enum TextContentAlignment {
    LEFT = 'left',
    CENTER = 'center',
    RIGHT = 'right',
}

type TextContentProps = {
    text: string;
    alignment: TextContentAlignment;
};

type ImageContentProps = {
    url: string;
    altText?: string;
    captionText?: string;
};

type VideoContentProps = {
    url: string;
    altText?: string;
};

type Admat = {
    imageUrl: string;
    altText?: string;
};

const getTextAlignment = (alignment: TextContentAlignment) => {
    switch (alignment) {
        case TextContentAlignment.LEFT:
            return 'eds-text--left';
        case TextContentAlignment.CENTER:
            return 'eds-text--center';
        case TextContentAlignment.RIGHT:
            return 'eds-text--right';
    }
};

const ContentWrapper = ({
    style,
    children,
}: {
    style?: string;
    children?: ReactNode;
}) => {
    const className = `eds-l-mar-vert-6 eds-l-sm-mar-vert-4 eds-text-bm ${style}`;

    return <div className={className}>{children}</div>;
};

const TextContent = ({ text, alignment }: TextContentProps) => (
    <ContentWrapper style="structured-content-rich-text">
        <div
            className={getTextAlignment(alignment)}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: text }}
        />
    </ContentWrapper>
);

const LazyContent: React.FC = ({ children }) => {
    const LAZY_ELEMENT_ROOT_MARGIN = '100px';
    const lazyElememt = useRef(null);

    const [isIntersecting] = useIntersecting({
        ref: lazyElememt,
        rootMargin: LAZY_ELEMENT_ROOT_MARGIN,
    });

    return (
        <div ref={lazyElememt}>
            {isIntersecting && (
                <div className="eds-fx--fade-in">{children}</div>
            )}
        </div>
    );
};

const VideoContent = ({ url, altText }: VideoContentProps) => (
    <ContentWrapper style="structured-content-video">
        <iframe
            className="structured-content-video__iframe"
            src={url}
            frameBorder="0"
            allowFullScreen
            title={altText || gettext('Video content').toString()}
            loading="lazy"
        >
            {altText}
        </iframe>
    </ContentWrapper>
);

const ImageContent = ({ url, altText, captionText }: ImageContentProps) => (
    <ContentWrapper>
        <LazyContent>
            <img
                className="eds-max-img"
                src={url}
                alt={altText || ''}
                loading="lazy"
            />
        </LazyContent>
        {captionText && (
            <span className="eds-text-bs eds-text-color--ui-600">
                {captionText}
            </span>
        )}
    </ContentWrapper>
);

const Admat = ({ imageUrl, altText }: Admat) => (
    <ImageContent url={imageUrl} altText={altText || ''} />
);

const StructuredModuleRenderer = ({
    module,
}: {
    module: StructuredContentModule;
}) => {
    switch (module.type) {
        case StructuredContentModuleType.TEXT:
            const textContent = module as TextContentProps;
            return (
                <TextContent
                    text={textContent.text}
                    alignment={textContent.alignment}
                />
            );
        case StructuredContentModuleType.IMAGE:
            const imageContent = module as ImageContentProps;
            return (
                <ImageContent
                    url={imageContent.url}
                    altText={imageContent.altText}
                    captionText={imageContent.captionText}
                />
            );
        case StructuredContentModuleType.VIDEO:
            const videoContent = module as VideoContentProps;
            return (
                <VideoContent
                    url={videoContent.url}
                    altText={videoContent.altText}
                />
            );
        default:
            return <></>;
    }
};

export const AboutThisEvent = ({
    hasStructuredContent,
    admat,
    modules,
    widgets,
    highlights,
}: AboutThisEventProps) => {
    const hasHighlights =
        highlights && Object.values(highlights).includes(true);

    if (modules.length === 0 && widgets.length === 0 && !hasHighlights) {
        return null;
    }

    const { agenda: agendaWidget, faqs: faqWidget } = parseWidgets(widgets);

    return (
        <>
            {(hasStructuredContent || hasHighlights) && (
                <div className="eds-l-mar-bot-8 structured-content">
                    {admat && (
                        <Admat
                            imageUrl={admat.imageUrl}
                            altText={admat.altText}
                        />
                    )}

                    {!!agendaWidget && (
                        <div className="structured-content-agenda-widget">
                            <EventDetailsSectionTitle>
                                <h2>{gettext('Agenda')}</h2>
                            </EventDetailsSectionTitle>
                            <Agenda agenda={agendaWidget as AgendaWidget} />
                        </div>
                    )}

                    <EventDetailsSectionTitle>
                        <h2>{gettext('About this event')}</h2>
                    </EventDetailsSectionTitle>
                    {hasHighlights && <Highlights highlights={highlights} />}
                    <div className="has-user-generated-content">
                        {modules.map((module, index) => (
                            <StructuredModuleRenderer
                                module={module}
                                key={index}
                            />
                        ))}
                    </div>

                    {!!faqWidget && (
                        <div className="structured-content-agenda-widget">
                            <EventDetailsSectionTitle>
                                <h2>{gettext('Frequently asked questions')}</h2>
                            </EventDetailsSectionTitle>
                            <Faq data={faqWidget as FaqWidget} />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export const OldDescriptionBeforeStructuredContent = ({
    legacyDescription,
    highlights,
}: LegacyDescription) => {
    const hasHighlights =
        highlights && Object.values(highlights).includes(true);

    return (
        <div className="eds-l-mar-bot-8 eds-l-pad-bot-6 old-description">
            <EventDetailsSectionTitle>
                <h2>{gettext('Description')}</h2>
            </EventDetailsSectionTitle>
            {hasHighlights && <Highlights highlights={highlights} />}
            <div className="eds-text-bm">
                {/* Summary comes from an old creation flow */}
                <div
                    className="eds-l-mar-top-3 description-items"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: legacyDescription }}
                ></div>
            </div>
        </div>
    );
};
